<template>
    <section class="flex flex-col gap-5">
        <header class="flex flex-col gap-2">
            <h2 class="title-subsection text-ink">{{ isEdit ? t("reflections.admin.newCampaign.titleEdit") : t("reflections.admin.newCampaign.titleNew") }}</h2>
            <p class="body-small text-ink-muted">
                {{ isEdit ? t("reflections.admin.newCampaign.subtitleEdit") : t("reflections.admin.newCampaign.subtitleNew") }}
            </p>
        </header>

        <HorizontalDivider as="hr" spacing="none" />

        <BaseButton theme="tertiary" size="sm" class="self-start" @click="emit('back')"> <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("common.back") }} </BaseButton>

        <BaseCard padding="lg">
            <form class="flex flex-col gap-6" @submit.prevent="submit">
                <label for="campaign-name" class="flex flex-col gap-2">
                    <span class="body-small-bold text-ink">{{ t("reflections.admin.newCampaign.nameLabel") }}</span>
                    <input
                        id="campaign-name"
                        v-model="form.name"
                        type="text"
                        required
                        maxlength="255"
                        :placeholder="t('reflections.admin.newCampaign.namePlaceholder')"
                        class="w-full px-3 py-2 border border-control-border rounded-lgr outline-none focus:border-control-border-focus body-small" />
                    <span class="details text-ink-muted">{{ t("reflections.admin.newCampaign.nameHelp") }}</span>
                </label>

                <HorizontalDivider as="hr" spacing="none" />

                <fieldset class="flex flex-col gap-2">
                    <legend class="body-small-bold text-ink mb-2">{{ t("reflections.admin.newCampaign.typeLegend") }}</legend>
                    <label for="campaign-type-360" class="flex items-start gap-3 cursor-pointer">
                        <input id="campaign-type-360" v-model="form.isSelfOnly" type="radio" name="campaign-type" :value="false" :disabled="!typeAllowed(false)" class="mt-1" />
                        <span class="flex flex-col gap-1">
                            <span class="body-small-bold text-ink">{{ t("reflections.admin.newCampaign.type360Title") }}</span>
                            <span class="body-small text-ink-muted">{{ t("reflections.admin.newCampaign.type360Help") }}</span>
                        </span>
                    </label>
                    <label for="campaign-type-self" class="flex items-start gap-3 cursor-pointer">
                        <input id="campaign-type-self" v-model="form.isSelfOnly" type="radio" name="campaign-type" :value="true" :disabled="!typeAllowed(true)" class="mt-1" />
                        <span class="flex flex-col gap-1">
                            <span class="body-small-bold text-ink">{{ t("reflections.admin.newCampaign.typeSelfTitle") }}</span>
                            <span class="body-small text-ink-muted">{{ t("reflections.admin.newCampaign.typeSelfHelp") }}</span>
                        </span>
                    </label>
                </fieldset>

                <HorizontalDivider as="hr" spacing="none" />

                <label for="campaign-template" class="flex flex-col gap-2">
                    <span class="body-small-bold text-ink">{{ t("reflections.admin.newCampaign.templateLabel") }}</span>
                    <select
                        id="campaign-template"
                        v-model="form.templateId"
                        required
                        :disabled="isEdit"
                        class="w-full px-3 py-2 border border-control-border rounded-lgr outline-none focus:border-control-border-focus body-small disabled:text-ink-muted">
                        <option v-for="templateOption in eligibleTemplates" :key="templateOption.id" :value="templateOption.id">{{ templateOption.name }}</option>
                    </select>
                    <span v-if="isEdit" class="details text-ink-muted">{{ t("reflections.admin.newCampaign.templateLocked") }}</span>
                    <span v-else-if="eligibleTemplates.length === 0" class="details text-status-error">
                        {{ t("reflections.admin.newCampaign.noTemplates") }}
                    </span>
                    <span v-else-if="selectedTemplate" class="details text-ink-muted">
                        {{ t("reflections.admin.newCampaign.questionCount", { count: selectedTemplate.question_count }) }}
                    </span>
                </label>

                <template v-if="!form.isSelfOnly">
                    <HorizontalDivider as="hr" spacing="none" />
                    <label for="campaign-min-responses" class="flex flex-col gap-2">
                        <span class="body-small-bold text-ink">{{ t("reflections.admin.newCampaign.minResponsesLabel") }}</span>
                        <input
                            id="campaign-min-responses"
                            v-model.number="form.minResponses"
                            type="number"
                            min="0"
                            max="32767"
                            required
                            class="w-32 px-3 py-2 border border-control-border rounded-lgr outline-none focus:border-control-border-focus body-small" />
                        <span class="details text-ink-muted">{{ t("reflections.admin.newCampaign.minResponsesHelp") }}</span>
                    </label>

                    <HorizontalDivider as="hr" spacing="none" />

                    <label for="campaign-self-assess" class="flex items-start gap-3 cursor-pointer">
                        <input id="campaign-self-assess" v-model="form.selfAssessEnabled" type="checkbox" class="mt-1" />
                        <span class="flex flex-col gap-1">
                            <span class="body-small-bold text-ink">{{ t("reflections.admin.newCampaign.selfAssessTitle") }}</span>
                            <span class="body-small text-ink-muted">{{ t("reflections.admin.newCampaign.selfAssessHelp") }}</span>
                        </span>
                    </label>
                </template>

                <HorizontalDivider as="hr" spacing="none" />

                <FormField
                    id="campaign-closes-at"
                    :label="t('reflections.admin.newCampaign.closeDateLabel')"
                    :note="t('reflections.admin.newCampaign.optional')"
                    :hint="t('reflections.admin.newCampaign.closeDateHelp')">
                    <BaseInput v-model="form.closesOn" type="date" size="compact" :min="todayIso" />
                </FormField>

                <BaseNotification v-if="submitError" theme="error" :text="submitError" hide-dismiss />
            </form>
        </BaseCard>

        <div class="flex items-center justify-end gap-3 sticky bottom-4">
            <BaseButton theme="neutral" size="sm" :disabled="submitting" @click="emit('back')">{{ t("common.cancel") }}</BaseButton>
            <BaseButton theme="primary" size="sm" :disabled="submitting || !canSubmit" @click="submit">{{ submitLabel }}</BaseButton>
        </div>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { AdminCampaignResponse, AdminCampaignSummary, CreateCampaignRequest, TemplateSummary, UpdateCampaignRequest } from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import FormField from "~vue/components/form/FormField.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    templates: TemplateSummary[];
    // When set, the form edits this draft in place instead of creating one.
    campaign?: AdminCampaignSummary | null;
}

const props = defineProps<Props>();
const { t } = useI18n();
const emit = defineEmits<{
    (event: "back"): void;
    (event: "created", campaign: AdminCampaignSummary): void;
    (event: "saved", campaign: AdminCampaignSummary): void;
}>();

const submitting = ref(false);
const submitError = ref<string | null>(null);
const isEdit = props.campaign != null;

// closes_at is stored as the end of the chosen day, so read the local date back.
function toLocalDateInput(iso: string | null | undefined): string {
    if (!iso) {
        return "";
    }
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const firstDefaultTemplate = props.templates.find((template) => template.assessment_mode !== "self_only") ?? props.templates[0] ?? null;
const form = ref(
    props.campaign
        ? {
              name: props.campaign.name,
              isSelfOnly: props.campaign.is_self_assessment_only,
              templateId: props.campaign.template.id,
              minResponses: props.campaign.min_responses ?? 0,
              selfAssessEnabled: props.campaign.self_assess_enabled ?? true,
              closesOn: toLocalDateInput(props.campaign.closes_at),
          }
        : {
              name: "",
              isSelfOnly: false,
              templateId: firstDefaultTemplate?.id ?? null,
              minResponses: firstDefaultTemplate?.min_responses ?? 0,
              selfAssessEnabled: true,
              closesOn: "",
          },
);

const initial = { ...form.value };

// The template is fixed while editing, so a type it doesn't support is off limits.
function typeAllowed(selfOnly: boolean): boolean {
    const mode = props.campaign?.template.assessment_mode;
    if (!mode) {
        return true;
    }
    return selfOnly ? mode !== "rater_360" : mode !== "self_only";
}

const submitLabel = computed(() => {
    if (isEdit) {
        return submitting.value ? t("reflections.admin.newCampaign.saving") : t("reflections.admin.newCampaign.saveChanges");
    }
    return submitting.value ? t("reflections.admin.newCampaign.creating") : t("reflections.admin.newCampaign.create");
});

const todayIso = new Date().toISOString().slice(0, 10);
const eligibleTemplates = computed<TemplateSummary[]>(() => {
    if (props.campaign) {
        return [props.campaign.template];
    }
    return props.templates.filter((template) => (form.value.isSelfOnly ? template.assessment_mode !== "rater_360" : template.assessment_mode !== "self_only"));
});
const selectedTemplate = computed<TemplateSummary | null>(() => eligibleTemplates.value.find((template) => template.id === form.value.templateId) ?? null);
const canSubmit = computed(() => form.value.name.trim().length > 0 && selectedTemplate.value !== null && form.value.minResponses >= 0);

watch(eligibleTemplates, (eligible) => {
    if (!eligible.some((template) => template.id === form.value.templateId)) {
        form.value.templateId = eligible[0]?.id ?? null;
    }
});

watch(selectedTemplate, (template) => {
    if (template) {
        form.value.minResponses = template.min_responses;
    }
});

// A self-only draft carries a zero minimum; coming back to 360 should not
// keep it, or participants could unlock results with no raters. A minimum
// the admin set while the type was 360 survives a round trip through
// self-only, including a deliberate zero.
let lastMinResponsesFor360: number | null = form.value.isSelfOnly ? null : form.value.minResponses;
watch(
    () => form.value.isSelfOnly,
    (selfOnly) => {
        if (selfOnly) {
            lastMinResponsesFor360 = form.value.minResponses;
            return;
        }
        if (lastMinResponsesFor360 !== null) {
            form.value.minResponses = lastMinResponsesFor360;
        } else if (form.value.minResponses === 0 && selectedTemplate.value) {
            form.value.minResponses = selectedTemplate.value.min_responses;
        }
    },
);

function payload(): Omit<CreateCampaignRequest, "template_id"> {
    return {
        name: form.value.name.trim(),
        is_self_assessment_only: form.value.isSelfOnly,
        min_responses: form.value.isSelfOnly ? 0 : form.value.minResponses,
        self_assess_enabled: form.value.isSelfOnly ? true : form.value.selfAssessEnabled,
        closes_at: form.value.closesOn ? new Date(`${form.value.closesOn}T23:59:59`).toISOString() : null,
    };
}

// Only what the admin changed goes out, so a save from an older form can't
// overwrite another admin's edit, and an untouched close date is not
// rebuilt in this browser's timezone. The type governs the rater fields, so
// a type change sends those too.
function patchBody(): UpdateCampaignRequest {
    const full = payload();
    const body: UpdateCampaignRequest = {};
    const typeChanged = form.value.isSelfOnly !== initial.isSelfOnly;
    if (form.value.name.trim() !== initial.name.trim()) {
        body.name = full.name;
    }
    if (typeChanged) {
        body.is_self_assessment_only = full.is_self_assessment_only;
    }
    if (typeChanged || form.value.minResponses !== initial.minResponses) {
        body.min_responses = full.min_responses;
    }
    if (typeChanged || form.value.selfAssessEnabled !== initial.selfAssessEnabled) {
        body.self_assess_enabled = full.self_assess_enabled;
    }
    if (form.value.closesOn !== initial.closesOn) {
        body.closes_at = full.closes_at;
    }
    return body;
}

async function submit() {
    if (!canSubmit.value) {
        return;
    }
    submitting.value = true;
    submitError.value = null;
    try {
        const request = props.campaign
            ? useFetch(`/v1/reflections/admin/campaigns/${props.campaign.id}`).patch(JSON.stringify(patchBody()), "application/json")
            : useFetch("/v1/reflections/admin/campaigns").post(JSON.stringify({ ...payload(), template_id: form.value.templateId }), "application/json");
        const { data, error: fetchError } = await request.json<AdminCampaignResponse>();
        if (fetchError.value) {
            submitError.value =
                (data.value as { error?: string } | null)?.error || (isEdit ? t("reflections.admin.newCampaign.saveFailed") : t("reflections.admin.newCampaign.createFailed"));
            return;
        }
        if (data.value && isEdit) {
            emit("saved", data.value.campaign);
        } else if (data.value) {
            emit("created", data.value.campaign);
        }
    } finally {
        submitting.value = false;
    }
}
</script>
